import { useCallback, useEffect, useRef, useState } from "react";
import { Globe, Copy, Check, RefreshCw, ShieldCheck, CheckCircle2, AlertTriangle, Loader2, Unlink, ExternalLink, Store, Circle, HelpCircle, ClipboardCopy } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDateTime } from "../../lib/api";
import { PageHeader, ConfirmDialog } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const Card = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`bg-card border border-border rounded-2xl p-6 sm:p-8 ${className}`}>{children}</div>
);

const SSL_LABEL = { active: "SSL Active", provisioning: "SSL Provisioning", pending: "SSL Pending", error: "SSL Error" };
const DNS_LABEL = { waiting: "Waiting for DNS", conflict: "DNS Conflict Detected", verified: "DNS Verified" };
const INSTRUCTIONS = [
  "Log in to your domain provider (GoDaddy, Hostinger, Namecheap, Cloudflare — wherever you bought the domain).",
  "Open your domain.",
  "Open DNS Management / DNS Records.",
  "Add or edit the DNS records shown above — nothing else needs to change.",
  "Save the changes.",
  "Return to MILTROO.",
  "Click Verify Domain. We also re-check automatically.",
];

export default function StoreDomain() {
  const [websites, setWebsites] = useState(null);
  const [selWid, setSelWid] = useState("");
  const [data, setData] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/store-admin/domain/websites").then((r) => {
      setWebsites(r.data.websites);
      if (r.data.websites.length && !selWid) setSelWid(r.data.websites[0].id);
    }).catch((e) => toast.error(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (!selWid) return;
    api.get("/store-admin/domain", { params: { website_id: selWid } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [selWid]);
  useEffect(() => { load(); }, [load]);

  const domain = data?.domain?.custom ? data.domain : null;
  const pending = domain && domain.status === "pending";
  const connected = domain && (domain.status === "connected" || domain.status === "verified");

  const verify = useCallback(async (silent = false) => {
    if (verifying || !selWid) return;
    setVerifying(true);
    try {
      await api.post("/store-admin/domain/verify", { website_id: selWid });
      setNote("");
      toast.success("Domain verified — connecting your website");
      load();
    } catch (e) {
      if (!silent) setNote(errMsg(e));
      load();
    } finally { setVerifying(false); }
  }, [verifying, selWid, load]);

  useEffect(() => {
    if (!pending) { clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(() => verify(true), 10000);
    return () => clearInterval(pollRef.current);
  }, [pending, verify]);

  const connect = async () => {
    if (!input.trim()) { toast.error("Enter your domain first — e.g. yourstore.com"); return; }
    setBusy(true);
    try {
      const r = await api.post("/store-admin/domain/connect", { domain: input.trim(), website_id: selWid });
      setData(r.data); setNote("");
      toast.success("Domain saved — add the DNS records below at your provider");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const disconnect = async () => {
    try {
      await api.post("/store-admin/domain/disconnect", { website_id: selWid });
      toast.success("Domain disconnected — your MILTROO store link keeps working");
      setInput(""); setNote(""); load();
      api.get("/store-admin/domain/websites").then((r) => setWebsites(r.data.websites)).catch(() => {});
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copy = async (key, val) => {
    try { await navigator.clipboard.writeText(val); } catch { /* clipboard blocked */ }
    setCopied(key); setTimeout(() => setCopied(""), 1800);
  };

  const copyAll = async () => {
    const txt = (data.records || []).map((r) => `Type: ${r.type} | Name: ${r.name} | Value: ${r.value} | TTL: Automatic / 1 Hour`).join("\n");
    try { await navigator.clipboard.writeText(txt); } catch { /* clipboard blocked */ }
    setCopied("all"); setTimeout(() => setCopied(""), 1800);
  };

  if (!websites) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  const records = [...(data?.records || [])].sort((a, b) => (a.type === "CNAME" ? -1 : 1) - (b.type === "CNAME" ? -1 : 1));
  const conflict = domain?.conflict;
  const steps = domain ? [
    ["Domain saved", true],
    ["DNS records added at your provider", domain.dns_status === "verified"],
    ["Domain verified", domain.dns_status === "verified"],
    ["SSL secured", connected && domain.ssl_status === "active"],
    ["Website live on your domain", connected],
  ] : [];

  return (
    <div data-testid="domain-page" className="max-w-3xl">
      <PageHeader title="Custom Domain" subtitle="Connect a domain you own to your store — works with any domain provider. No transfer needed." testid="domain-title" />

      {/* ================= WEBSITE / STORE SELECTOR ================= */}
      {websites.length > 1 && (
        <Card testid="website-selector" className="mb-6">
          <p className="text-xs uppercase tracking-widest font-extrabold text-muted-foreground mb-3">Select Website / Store</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {websites.map((w) => (
              <button key={w.id} data-testid={`website-option-${w.slug}`} onClick={() => { setSelWid(w.id); setData(null); setNote(""); }}
                className={`text-left border-2 rounded-xl px-4 py-3.5 transition-all ${selWid === w.id ? "border-primary shadow-md" : "border-border hover:border-primary/40"}`}>
                <p className="font-bold text-sm flex items-center gap-2"><Store className="h-4 w-4 text-primary" /> {w.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{w.domain ? `${w.domain} · ${w.domain_status}` : "No domain connected"}</p>
              </button>
            ))}
          </div>
        </Card>
      )}

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          {/* ================= STEP: ENTER DOMAIN ================= */}
          {!domain && (
            <Card testid="domain-connect-card">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><Globe className="h-5 w-5 text-primary" /></div>
                <div>
                  <p className="font-display font-bold text-lg">Connect Your Custom Domain</p>
                  <p className="text-sm text-muted-foreground">to <span className="font-bold text-foreground">{data.website.name}</span> — your MILTROO store link keeps working as a backup.</p>
                </div>
              </div>
              <label className="text-sm font-semibold">Enter Your Custom Domain</label>
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <Input data-testid="domain-input" value={input} onChange={(e) => setInput(e.target.value)}
                  placeholder="yourstore.com" className="h-12 font-mono text-base"
                  onKeyDown={(e) => e.key === "Enter" && connect()} />
                <Button data-testid="continue-button" onClick={connect} disabled={busy}
                  className="h-12 px-8 rounded-full font-bold shrink-0">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Your domain stays with your current provider — you only add 2 simple DNS records there.</p>
            </Card>
          )}

          {/* ================= PENDING: DNS RECORDS + VERIFY ================= */}
          {pending && (
            <div className="space-y-6">
              <Card testid="domain-progress-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-extrabold text-muted-foreground">Connecting</p>
                    <p className="font-display font-extrabold text-2xl mt-1" data-testid="pending-domain-name">{domain.custom}</p>
                    <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2"><Store className="h-3.5 w-3.5" /> {data.website.name}</p>
                  </div>
                  <span className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-extrabold ${domain.dns_status === "conflict" ? "bg-red-500/10 text-red-600 dark:text-red-400" : domain.dns_status === "verified" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/10 text-amber-600 dark:text-amber-400"}`} data-testid="domain-status">
                    {domain.dns_status === "conflict" ? <AlertTriangle className="h-3.5 w-3.5" /> : domain.dns_status === "verified" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {DNS_LABEL[domain.dns_status] || "Waiting for DNS"}
                  </span>
                </div>
                <div className="mt-6 space-y-2.5" data-testid="dns-status-steps">
                  {steps.map(([label, done], i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm" data-testid={`stage-${i}`}>
                      {done ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                        : i === steps.findIndex(([, d2]) => !d2) ? <Circle className="h-4 w-4 text-primary shrink-0" />
                        : <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />}
                      <span className={done ? "font-semibold" : "text-muted-foreground"}>{label}</span>
                    </div>
                  ))}
                </div>
                {domain.dns_status === "verified" && (
                  <div className="mt-4 flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4" data-testid="awaiting-ssl-note">
                    <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                    <p className="text-sm">DNS is verified — your domain points to MILTROO. Secure HTTPS activates once the domain is linked on MILTROO hosting (platform step). We keep checking automatically; nothing else is needed from you.</p>
                  </div>
                )}
              </Card>

              {conflict && (
                <div data-testid="conflict-panel" className="bg-red-500/10 border border-red-500/25 rounded-2xl p-5">
                  <p className="font-display font-bold text-red-600 dark:text-red-400 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> DNS Conflict Detected</p>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>Conflicting record: <span className="font-mono font-bold">{conflict.record}</span></p>
                    <p className="text-muted-foreground">It currently points to: <span className="font-mono font-bold text-foreground">{conflict.observed?.join(", ") || "another service"}</span></p>
                    <p className="text-muted-foreground">MILTROO needs it to point to: <span className="font-mono font-bold text-foreground">{conflict.expected?.join(", ")}</span></p>
                    {conflict.www && <p className="text-muted-foreground">Also check: <span className="font-mono font-bold text-foreground">{conflict.www.record}</span> → currently {conflict.www.observed?.join(", ") || "another service"}</p>}
                    <p className="text-xs">Update only that record at your provider. Do NOT delete anything else — your email (MX/TXT) and other records are not affected by MILTROO.</p>
                  </div>
                </div>
              )}

              {/* DNS records */}
              <Card testid="dns-records-card">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <p className="font-display font-bold text-lg">DNS Records Required</p>
                  <Button data-testid="copy-all-button" variant="outline" size="sm" className="rounded-full font-bold" onClick={copyAll}>
                    {copied === "all" ? <><Check className="h-3.5 w-3.5 mr-1.5" /> Copied</> : <><ClipboardCopy className="h-3.5 w-3.5 mr-1.5" /> Copy All DNS Records</>}
                  </Button>
                </div>
                <div className="space-y-3" data-testid="dns-records">
                  {records.map((r, i) => (
                    <div key={i} data-testid={`dns-record-${i}`} className="border border-border rounded-xl p-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="px-2.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-extrabold w-16 text-center">{r.type}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-muted-foreground">Host / Name: <span className="font-mono font-bold text-foreground">{r.name}</span></p>
                          <p className="text-xs text-muted-foreground break-all">Target / Value: <span className="font-mono font-bold text-foreground">{r.value}</span></p>
                          <p className="text-[10px] text-muted-foreground/70 mt-0.5">TTL: Automatic / 1 Hour · {r.purpose}</p>
                        </div>
                        <Button data-testid={`copy-record-${i}`} variant="outline" size="sm" className="rounded-full font-bold shrink-0"
                          onClick={() => copy(`r${i}`, r.value)}>
                          {copied === `r${i}` ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {data.apex_note && (
                  <div className="mt-4 flex items-start gap-3 bg-muted border border-border rounded-xl p-4" data-testid="apex-note">
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs leading-relaxed">{data.apex_note}</p>
                  </div>
                )}
              </Card>

              {/* Instructions + help */}
              <Card testid="dns-instructions">
                <p className="font-display font-bold mb-4">Update these DNS records at your domain provider</p>
                <ol className="space-y-2.5 text-sm">
                  {INSTRUCTIONS.map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-5 flex items-start gap-3 bg-muted border border-border rounded-xl p-4" data-testid="help-section">
                  <HelpCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    <span className="font-bold text-foreground">Where do I add these DNS records?</span> At the provider where you bought the domain — their DNS Management page. Your domain stays with your current provider; you do <span className="font-bold text-foreground">not</span> need to transfer it to MILTROO. DNS changes can take up to 24 hours — we re-check automatically.
                  </p>
                </div>
              </Card>

              {note && (
                <div data-testid="verify-note" className="flex items-start gap-3 bg-muted border border-border rounded-xl p-4">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-sm">{note}</p>
                </div>
              )}
              {domain.last_check?.reason && !note && (
                <p className="text-xs text-muted-foreground" data-testid="domain-last-check">Last check: {domain.last_check.reason}</p>
              )}

              <div className="flex flex-wrap gap-3">
                <Button data-testid="verify-domain-button" onClick={() => verify(false)} disabled={verifying} className="rounded-full font-bold h-11 px-7">
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  {domain.checked_at ? "Verify Again" : "Verify Domain"}
                </Button>
                <ConfirmDialog testid="disconnect-domain" title="Remove this domain?"
                  description={`${domain.custom} will be removed from ${data.website.name}. Your website, products, orders and customers stay safe, and your MILTROO store link keeps working. You can reconnect the domain anytime.`}
                  onConfirm={disconnect}
                  trigger={<Button data-testid="disconnect-domain-button" variant="outline" className="rounded-full font-bold h-11 px-7 text-destructive border-destructive/30 hover:bg-destructive/10"><Unlink className="h-4 w-4 mr-2" /> Disconnect Domain</Button>} />
              </div>
            </div>
          )}

          {/* ================= CONNECTED ================= */}
          {connected && (
            <div className="space-y-6">
              <Card testid="connected-card" className="border-emerald-500/30">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-display font-extrabold text-2xl flex items-center gap-2.5">
                      <CheckCircle2 className="h-6 w-6 text-emerald-500" /> {domain.custom}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1.5 flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> {data.website.name}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-extrabold" data-testid="domain-status">
                    <Check className="h-3.5 w-3.5" /> Connected
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-6">
                  {[
                    ["Store", data.website.name, true, undefined],
                    ["Custom Domain", domain.custom, true, undefined],
                    ["DNS", domain.dns_status === "verified" ? "Verified" : "Verified", true, "dns-status"],
                    ["SSL / HTTPS", SSL_LABEL[domain.ssl_status] || "SSL Pending", domain.ssl_status === "active", "ssl-status"],
                    ["Status", "Connected", true, undefined],
                    ["Connected Since", domain.connected_at ? fmtDateTime(domain.connected_at) : "—", true, undefined],
                  ].map(([label, val, ok, tid], i) => (
                    <div key={i} className="flex items-center justify-between border border-border rounded-xl px-4 py-3 gap-3">
                      <span className="text-xs font-bold text-muted-foreground shrink-0">{label}</span>
                      <span className="text-sm font-extrabold flex items-center gap-1.5 text-right min-w-0" data-testid={tid}>
                        {ok ? <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500 shrink-0" />}<span className="truncate">{val}</span>
                      </span>
                    </div>
                  ))}
                </div>
                {domain.hosts && (
                  <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="host-status-grid">
                    {[["www", domain.hosts.www], ["apex", domain.hosts.apex]].filter(([, h]) => h).map(([kind, h]) => (
                      <div key={kind} data-testid={`host-status-${kind}`} className="flex items-center justify-between border border-border rounded-xl px-4 py-3 gap-3">
                        <span className="text-xs font-bold text-muted-foreground font-mono truncate">{h.host}</span>
                        <span className={`text-xs font-extrabold flex items-center gap-1.5 shrink-0 ${h.serving ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                          {h.serving ? <Check className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                          {h.serving ? "HTTPS Live" : (h.ssl === "pending_validation" || h.ssl === "initializing" || h.ssl === "pending") ? "SSL Provisioning" : "Pending"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {domain.canonical_url && (
                  <p className="text-xs text-muted-foreground mt-3" data-testid="canonical-url-note">
                    Primary address: <span className="font-mono font-semibold text-foreground">{domain.canonical_url}</span> — the other address redirects here automatically.
                  </p>
                )}
                {domain.ssl_status !== "active" && (
                  <p className="text-xs text-muted-foreground mt-3" data-testid="ssl-reason">
                    SSL is being configured automatically{domain.ssl_reason ? ` (${domain.ssl_reason})` : ""}. Click Verify Again in a few minutes.
                  </p>
                )}
                {!domain.www_ok && (
                  <p className="text-xs text-muted-foreground mt-3" data-testid="www-note">
                    Tip: also add the CNAME record for <span className="font-mono font-bold">www</span> so www.{domain.custom} works too.
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5 flex-wrap">
                  <Globe className="h-3.5 w-3.5" /> Backup link (always works): <span className="font-mono font-semibold" data-testid="platform-url">{data.platform_url}</span>
                </p>
              </Card>
              <div className="flex flex-wrap gap-3">
                <a href={domain.canonical_url || (domain.www_ok ? `https://www.${domain.custom}` : `https://${domain.custom}`)} target="_blank" rel="noreferrer" data-testid="visit-website-button"
                  className="inline-flex items-center justify-center rounded-full font-bold h-11 px-7 text-sm bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
                  <ExternalLink className="h-4 w-4 mr-2" /> Visit Website
                </a>
                <Button data-testid="verify-again-button" onClick={() => verify(false)} disabled={verifying} variant="outline" className="rounded-full font-bold h-11 px-7">
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />} Verify Again
                </Button>
                <ConfirmDialog testid="disconnect-domain" title="Disconnect this domain?"
                  description={`${domain.custom} will stop opening ${data.website.name}. Your website, products, orders and customers stay safe, and your MILTROO store link keeps working.`}
                  onConfirm={disconnect}
                  trigger={<Button data-testid="disconnect-domain-button" variant="outline" className="rounded-full font-bold h-11 px-7 text-destructive border-destructive/30 hover:bg-destructive/10"><Unlink className="h-4 w-4 mr-2" /> Disconnect Domain</Button>} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
